"use client";

import { c as _c } from "react/compiler-runtime";
import { CollectionLockup, CollectionLockupContent, CollectionLockupFragment, CollectionLockupTitle, useBlockExplorer } from "@opensea/app-kit";
import { graphql, readFragment } from "@opensea/graphql";
import { Dropdown, Item, ItemAvatar, ItemContent } from "@opensea/next-ui-kit";
import { CopyToClipboard, classNames, DropdownItem, DropdownItemAvatar, DropdownItemContent, DropdownItemTitle, DropdownSeparator, Flex, Media, Separator, UnstyledButton } from "@opensea/ui-kit";
import { ContentCopy, Discord, Flag, Info, Instagram, MoreHoriz, Share, Star } from "@opensea/ui-kit/icons";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useCollectionLogoMediaPreview } from "@/components/collection/overview/components/CollectionOverviewPreview/components/CollectionHeaderPreview/useCollectionLogoMediaPreview";
import { CollectionPreviewLinks } from "@/components/collection/overview/components/CollectionOverviewPreview/components/CollectionPreviewLinks";
import { useHeroHeaderView } from "@/components/hero-header/HeroHeaderViewProvider";
import { getSocialAccountUrl, SocialLinks } from "@/components/social/SocialLinks";
import { EnforcementBadge, EnforcementBadgeFragment } from "@/components/trustandsafety/EnforcementBadge";
import { EntityType } from "@/components/trustandsafety/types";
import { useAddress } from "@/hooks/wallet";
import { getValidUrl } from "@/lib/helpers/url";
import { ReportModal } from "../../trustandsafety/ReportModal";
import { useCollectionLayout } from "../CollectionLayoutProvider";
import { CollectionMetadataChips } from "../CollectionMetadataChips";
import { CollectionMetadataChipsFragment } from "../CollectionMetadataChips/CollectionMetadataChips.fragment";
import { CollectionHeaderWatchlistButton, CollectionHeaderWatchlistButtonFragment } from "../CollectionWatchlistButton/CollectionHeaderWatchlistButton";
import { CollectionDescriptionModal } from "./CollectionDescriptionModal";
import { CollectionInfoEditButton, CollectionInfoEditButtonFragment } from "./CollectionInfoEditButton";
import { CollectionLogoEditActions } from "./CollectionLogoEditActions";
import { CollectionShareModal } from "./CollectionShareModal";
import { CollectionShareModalFragment } from "./CollectionShareModal.fragment";
import { collectionInfoVariants } from "./collection-info-variants";
export const CollectionInfoFragment = graphql(`
    fragment CollectionInfo on Collection {
      ...CollectionLockup
      imageUrl
      instagramUsername
      discordUrl
      externalUrl
      twitterUsername
      slug
      description
      chain {
        identifier
        arch
      }
      address
      enforcement {
        isDisabled
      }
      ...CollectionMetadataChips
      ...EnforcementBadge
      ...CollectionInfoEditButton
      ...CollectionShareModal
      ...CollectionHeaderWatchlistButton
    }
  `, [CollectionLockupFragment, CollectionMetadataChipsFragment, EnforcementBadgeFragment, CollectionInfoEditButtonFragment, CollectionShareModalFragment, CollectionHeaderWatchlistButtonFragment]);
export function CollectionInfo(t0) {
  const $ = _c(82);
  const {
    className,
    collection: collectionKey
  } = t0;
  let t1;
  if ($[0] !== collectionKey) {
    t1 = readFragment(CollectionInfoFragment, collectionKey);
    $[0] = collectionKey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const collection = t1;
  const blockExplorer = useBlockExplorer(collection.chain.identifier);
  const {
    headerView
  } = useHeroHeaderView();
  const {
    setAvatarRef,
    isPreview
  } = useCollectionLayout();
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const address = useAddress();
  const hasDescription = Boolean(collection.description);
  const t = useTranslations("CollectionInfo");
  const logoPreviewUrl = useCollectionLogoMediaPreview();
  const logoUrl = logoPreviewUrl || collection.imageUrl;
  let T0;
  let T1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[2] !== className || $[3] !== headerView || $[4] !== isPreview || $[5] !== logoUrl || $[6] !== setAvatarRef) {
    const {
      avatar,
      itemContent
    } = collectionInfoVariants({
      headerView
    });
    T1 = Item;
    if ($[13] !== className) {
      t3 = classNames("min-w-0 select-text", className);
      $[13] = className;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    t4 = "unstyled";
    const t6 = <ItemAvatar boost={2} border={true} className={avatar()} objectFit="cover" priority={true} ref={setAvatarRef} size={headerView === "compact" ? 46 : 60} src={logoUrl ?? undefined} />;
    let t7;
    if ($[15] !== isPreview) {
      t7 = isPreview && <CollectionLogoEditActions />;
      $[15] = isPreview;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    if ($[17] !== t6 || $[18] !== t7) {
      t5 = <Flex className="group relative">{t6}{t7}</Flex>;
      $[17] = t6;
      $[18] = t7;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    T0 = ItemContent;
    t2 = itemContent();
    $[2] = className;
    $[3] = headerView;
    $[4] = isPreview;
    $[5] = logoUrl;
    $[6] = setAvatarRef;
    $[7] = T0;
    $[8] = T1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    T0 = $[7];
    T1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
  }
  let t6;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <CollectionLockupContent><CollectionLockupTitle weight="semibold" /></CollectionLockupContent>;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== collection) {
    t7 = <Media lessThan="md"><CollectionLockup collection={collection} size="2xl">{t6}</CollectionLockup></Media>;
    $[21] = collection;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const t8 = headerView === "compact" ? "2xl" : "3xl";
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <CollectionLockupContent><CollectionLockupTitle weight="semibold" /></CollectionLockupContent>;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== collection || $[25] !== t8) {
    t10 = <Media greaterThanOrEqual="md"><CollectionLockup collection={collection} size={t8}>{t9}</CollectionLockup></Media>;
    $[24] = collection;
    $[25] = t8;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t7) {
    t11 = <h1>{t7}{t10}</h1>;
    $[27] = t10;
    $[28] = t7;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== collection) {
    t12 = <CollectionHeaderWatchlistButton collection={collection} />;
    $[30] = collection;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t11 || $[33] !== t12) {
    t13 = <Flex className="min-w-0 items-center gap-2">{t11}{t12}</Flex>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== collection) {
    t14 = <EnforcementBadge className="ml-2" entity={collection} />;
    $[35] = collection;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Media greaterThanOrEqual="md"><Flex className="h-full items-center"><Separator className="mx-4 max-h-6 w-[2px]" orientation="vertical" /></Flex></Media>;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== address || $[39] !== blockExplorer || $[40] !== collection || $[41] !== hasDescription || $[42] !== isPreview || $[43] !== t) {
    t16 = isPreview ? <CollectionPreviewLinks /> : <Flex className="items-center gap-5"><CollectionInfoEditButton collection={collection} />{hasDescription ? <UnstyledButton onClick={() => {
        setIsDescriptionModalOpen(true);
      }}><Info size={20} /></UnstyledButton> : null}{collection.address ? <CopyToClipboard text={collection.address} tooltipText="Copy address"><ContentCopy size={20} /></CopyToClipboard> : null}{collection.externalUrl ? <SocialLinks.Website href={getValidUrl(collection.externalUrl)} /> : null}{collection.twitterUsername ? <SocialLinks.Twitter username={collection.twitterUsername} /> : null}<UnstyledButton onClick={() => setIsShareModalOpen(true)}><Share size={20} /></UnstyledButton><Dropdown content={<><DropdownItem className="hidden"><DropdownItemAvatar icon={Star} /><DropdownItemContent><DropdownItemTitle>{t("watchlist")}</DropdownItemTitle></DropdownItemContent></DropdownItem>{collection.address ? <DropdownItem href={blockExplorer.getAddressUrl(collection.address)}><DropdownItemAvatar {...blockExplorer.getLogo()} /><DropdownItemContent><DropdownItemTitle>{t("viewOn", {
                name: blockExplorer.getName()
              })}</DropdownItemTitle></DropdownItemContent></DropdownItem> : null}<DropdownItem className="hidden"><DropdownItemAvatar icon={Flag} /><DropdownItemContent><DropdownItemTitle>{t("report")}</DropdownItemTitle></DropdownItemContent></DropdownItem>{collection.instagramUsername ? <DropdownItem href={getSocialAccountUrl("instagram", collection.instagramUsername)}><DropdownItemAvatar icon={Instagram} /><DropdownItemContent><DropdownItemTitle>{t("instagram")}</DropdownItemTitle></DropdownItemContent></DropdownItem> : null}{collection.discordUrl ? <DropdownItem href={collection.discordUrl}><DropdownItemAvatar icon={Discord} /><DropdownItemContent><DropdownItemTitle>{t("discord")}</DropdownItemTitle></DropdownItemContent></DropdownItem> : null}{address ? <><DropdownSeparator /><DropdownItem onClick={() => setIsReportModalOpen(true)}><DropdownItemAvatar icon={Flag} /><DropdownItemContent><DropdownItemTitle>{t("report")}</DropdownItemTitle></DropdownItemContent></DropdownItem></> : null}</>} overrides={{
        Content: {
          className: "w-auto"
        }
      }}><UnstyledButton><MoreHoriz size={20} /></UnstyledButton></Dropdown></Flex>;
    $[38] = address;
    $[39] = blockExplorer;
    $[40] = collection;
    $[41] = hasDescription;
    $[42] = isPreview;
    $[43] = t;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t13 || $[46] !== t14 || $[47] !== t16) {
    t17 = <Flex className="w-full min-w-0 flex-col items-start gap-2 md:flex-row md:items-center md:gap-0">{t13}{t14}{t15}{t16}</Flex>;
    $[45] = t13;
    $[46] = t14;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== collection) {
    t18 = <Media lessThan="3xl"><CollectionMetadataChips collection={collection} variant="compact" /></Media>;
    $[49] = collection;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  const t19 = headerView === "compact" ? "compact" : "full";
  let t20;
  if ($[51] !== collection || $[52] !== t19) {
    t20 = <Media greaterThanOrEqual="3xl"><CollectionMetadataChips collection={collection} variant={t19} /></Media>;
    $[51] = collection;
    $[52] = t19;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== t18 || $[55] !== t20) {
    t21 = <Media greaterThanOrEqual="md">{t18}{t20}</Media>;
    $[54] = t18;
    $[55] = t20;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== address || $[58] !== collection.slug || $[59] !== isReportModalOpen) {
    t22 = address ? <ReportModal entityId={collection.slug} entityType={EntityType.COLLECTION} onOpenChange={setIsReportModalOpen} open={isReportModalOpen} /> : null;
    $[57] = address;
    $[58] = collection.slug;
    $[59] = isReportModalOpen;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== collectionKey || $[62] !== hasDescription || $[63] !== isDescriptionModalOpen) {
    t23 = hasDescription ? <CollectionDescriptionModal collection={collectionKey} onOpenChange={setIsDescriptionModalOpen} open={isDescriptionModalOpen} /> : null;
    $[61] = collectionKey;
    $[62] = hasDescription;
    $[63] = isDescriptionModalOpen;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  let t24;
  if ($[65] !== collection || $[66] !== isShareModalOpen) {
    t24 = <CollectionShareModal collection={collection} onOpenChange={setIsShareModalOpen} open={isShareModalOpen} />;
    $[65] = collection;
    $[66] = isShareModalOpen;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  let t25;
  if ($[68] !== T0 || $[69] !== t17 || $[70] !== t2 || $[71] !== t21 || $[72] !== t22 || $[73] !== t23 || $[74] !== t24) {
    t25 = <T0 className={t2}>{t17}{t21}{t22}{t23}{t24}</T0>;
    $[68] = T0;
    $[69] = t17;
    $[70] = t2;
    $[71] = t21;
    $[72] = t22;
    $[73] = t23;
    $[74] = t24;
    $[75] = t25;
  } else {
    t25 = $[75];
  }
  let t26;
  if ($[76] !== T1 || $[77] !== t25 || $[78] !== t3 || $[79] !== t4 || $[80] !== t5) {
    t26 = <T1 className={t3} variant={t4}>{t5}{t25}</T1>;
    $[76] = T1;
    $[77] = t25;
    $[78] = t3;
    $[79] = t4;
    $[80] = t5;
    $[81] = t26;
  } else {
    t26 = $[81];
  }
  return t26;
}